import React, { useEffect, useState } from 'react';
import Select from 'react-select';
import he from 'he';
import api from '../services/apiService';

const limit = 25;

const emptyFilterOptions = {
  accountTypes: [],
  customerNumbers: [],
  companies: [],
  cities: [],
  origins: [],
  categories: [],
  groups: [],
  statuses: [],
};

function buildRecipientParams(filters) {
  const params = {};

  if (filters.accountType) params.accountType = filters.accountType.value;
  if (filters.customerNumber) params.customerNumber = filters.customerNumber.value;
  if (filters.company) params.company = filters.company.value;
  if (filters.city) params.city = filters.city.value;
  if (filters.customerOrigin) params.customerOriginId = filters.customerOrigin.value;
  if (filters.customerCategory) params.customerCategoryId = filters.customerCategory.value;
  if (filters.customerGroup) params.customerGroupId = filters.customerGroup.value;
  if (filters.status) params.status = filters.status.value;

  return params;
}

async function fetchAllFilteredRecipients(filters) {
  const response = await api.get('/bulk-mail/recipient-ids', {
    params: buildRecipientParams(filters),
  });
  return (response.data.recipients || []).map(recipient => recipient.key);
}

function BulkMailTableForm() {
  const [filters, setFilters] = useState({
    accountType: null,
    customerNumber: null,
    company: null,
    city: null,
    customerOrigin: null,
    customerCategory: null,
    customerGroup: null,
    status: null,
  });
  const [serverFilterOptions, setServerFilterOptions] = useState(emptyFilterOptions);
  const [recipients, setRecipients] = useState([]);
  const [selected, setSelected] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [mailing, setMailing] = useState(false);
  const [msg, setMsg] = useState(null);

  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showTemplatePreview, setShowTemplatePreview] = useState(false);
  const [pipelines, setPipelines] = useState([]);
  const [selectedPipeline, setSelectedPipeline] = useState(null);
  const [stages, setStages] = useState([]);
  const [selectedStage, setSelectedStage] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [users, setUsers] = useState([]);
  const [selectedResponsibleUser, setSelectedResponsibleUser] = useState(null);
  const [pipelineInstanceName, setPipelineInstanceName] = useState('');

  useEffect(() => {
    api.get('/messages/messagetemplates')
      .then(response => setTemplates(response.data || []))
      .catch(() => setTemplates([]));
    api.get('/pipeline-definitions')
      .then(response => setPipelines(response.data || []))
      .catch(() => setPipelines([]));
    api.get('/users')
      .then(response => {
        const data = response.data;
        setUsers(Array.isArray(data) ? data : (data?.rows || data?.data || []));
      })
      .catch(() => setUsers([]));
    api.get('/bulk-mail/filter-options')
      .then(response => setServerFilterOptions({
        ...emptyFilterOptions,
        ...response.data,
      }))
      .catch(() => setServerFilterOptions(emptyFilterOptions));
  }, []);

  useEffect(() => {
    if (!selectedPipeline) {
      setStages([]);
      setSelectedStage(null);
      return;
    }

    api.get('/pipeline-stages', {
      params: { pipelineDefId: selectedPipeline.id },
    })
      .then(response => setStages(response.data || []))
      .catch(() => setStages([]));
  }, [selectedPipeline]);

  useEffect(() => {
    let active = true;
    setLoading(true);

    api.get('/bulk-mail/recipients', {
      params: {
        ...buildRecipientParams(filters),
        limit,
        page: currentPage,
      },
    })
      .then(response => {
        if (!active) return;
        setRecipients(response.data.rows || []);
        setTotalPages(response.data.pages || 1);
      })
      .catch(() => {
        if (!active) return;
        setRecipients([]);
        setTotalPages(1);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [filters, currentPage]);

  const updateFilter = (name, value) => {
    setFilters(current => {
      const next = { ...current, [name]: value };
      if (name === 'accountType') {
        next.status = null;
        if (value?.value === 'prospect') next.customerNumber = null;
      }
      return next;
    });
    setCurrentPage(1);
    setSelected([]);
  };

  const toggleSelect = key => {
    setSelected(current => (
      current.includes(key)
        ? current.filter(selectedKey => selectedKey !== key)
        : [...current, key]
    ));
  };

  const toggleAll = async () => {
    const allPageSelected = recipients.length > 0
      && recipients.every(recipient => selected.includes(recipient.key));

    if (allPageSelected) {
      const pageKeys = new Set(recipients.map(recipient => recipient.key));
      setSelected(current => current.filter(key => !pageKeys.has(key)));
      return;
    }

    setLoading(true);
    try {
      const keys = await fetchAllFilteredRecipients(filters);
      setSelected(keys);
    } catch (error) {
      setMsg(`Fehler beim Laden der Empfänger: ${error?.response?.data?.error || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    setMsg(null);

    if (!selectedTemplate || !selectedPipeline || !selectedStage) {
      setMsg('Bitte wähle eine Vorlage, Pipeline und Phase.');
      return;
    }
    if (!selected.length) {
      setMsg('Bitte wähle mindestens einen Empfänger aus.');
      return;
    }

    setMailing(true);
    try {
      const formData = new FormData();
      formData.append('templateId', selectedTemplate.id);
      formData.append('pipelineDefId', selectedPipeline.id);
      formData.append('stageId', selectedStage.id);
      formData.append('recipients', JSON.stringify(selected));

      if (selectedFile) formData.append('attachment', selectedFile);
      if (pipelineInstanceName.trim()) {
        formData.append('pipelineInstanceName', pipelineInstanceName.trim());
      }
      if (selectedResponsibleUser?.value) {
        formData.append('responsibleUser', selectedResponsibleUser.value);
      }

      const response = await api.post('/bulk-mail/send-and-pipeline', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      const { count, skipped = [] } = response.data;
      let resultMessage = `Mails an ${count} Hauptansprechpartner gestartet.`;
      if (skipped.length) {
        resultMessage += ` ${skipped.length} Empfänger wurden ohne Hauptansprechpartner mit E-Mail übersprungen.`;
      }
      setMsg(resultMessage);
      setSelected([]);
      setSelectedFile(null);
    } catch (error) {
      setMsg(`Fehler beim Versand: ${error?.response?.data?.error || error.message}`);
    } finally {
      setMailing(false);
    }
  };

  const handleFileChange = event => {
    setSelectedFile(event.target.files?.[0] || null);
  };

  const currentPageSelected = recipients.length > 0
    && recipients.every(recipient => selected.includes(recipient.key));

  return (
    <div style={{ margin: '0 10px' }}>
      <h2>Massenmailing</h2>

      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        margin: '30px 0 0',
      }}>
        <div style={{ width: 600, marginBottom: 11 }}>
          <label style={{ fontWeight: 600, marginBottom: 4, display: 'block' }}>Vorlage</label>
          <Select
            options={templates.map(template => ({ value: template.id, label: template.name }))}
            value={selectedTemplate
              ? { value: selectedTemplate.id, label: selectedTemplate.name }
              : null}
            onChange={option => {
              setSelectedTemplate(templates.find(template => template.id === option?.value) || null);
            }}
            placeholder="Vorlage auswählen"
            isClearable
          />
          {selectedTemplate && (
            <label style={{
              display: 'block',
              marginTop: 7,
              marginBottom: 3,
              fontSize: 15,
              cursor: 'pointer',
            }}>
              <input
                type="checkbox"
                checked={showTemplatePreview}
                onChange={() => setShowTemplatePreview(current => !current)}
                style={{ marginRight: 7 }}
              />
              Vorlagen-Vorschau anzeigen
            </label>
          )}
          {selectedTemplate?.bodyHtml && showTemplatePreview && (
            <div style={{
              marginTop: 7,
              background: '#f7f7f7',
              border: '1px solid #eee',
              borderRadius: 6,
              padding: 13,
              fontSize: 14,
              minHeight: 30,
            }}>
              <div
                dangerouslySetInnerHTML={{ __html: he.decode(selectedTemplate.bodyHtml) }}
              />
            </div>
          )}
        </div>

        <div style={{ width: 600, marginBottom: 11 }}>
          <label style={{ fontWeight: 600, marginBottom: 4, display: 'block' }}>
            Datei anhängen
          </label>
          <input type="file" onChange={handleFileChange} />
          {selectedFile && (
            <div style={{ marginTop: 5, fontSize: 14, color: '#555' }}>
              Angehängt: <strong>{selectedFile.name}</strong>
            </div>
          )}
        </div>

        <div style={{ width: 600, marginBottom: 11 }}>
          <label style={{ fontWeight: 600, marginBottom: 4, display: 'block' }}>Pipeline</label>
          <Select
            options={pipelines.map(pipeline => ({ value: pipeline.id, label: pipeline.name }))}
            value={selectedPipeline
              ? { value: selectedPipeline.id, label: selectedPipeline.name }
              : null}
            onChange={option => {
              setSelectedPipeline(
                pipelines.find(pipeline => pipeline.id === option?.value) || null
              );
            }}
            placeholder="Pipeline auswählen"
            isClearable
          />
        </div>

        <div style={{ width: 600 }}>
          <label style={{ fontWeight: 600, marginBottom: 4, display: 'block' }}>Phase</label>
          <Select
            options={stages.map(stage => ({ value: stage.id, label: stage.name }))}
            value={selectedStage
              ? { value: selectedStage.id, label: selectedStage.name }
              : null}
            onChange={option => {
              setSelectedStage(stages.find(stage => stage.id === option?.value) || null);
            }}
            placeholder="Phase auswählen"
            isClearable
            isDisabled={!selectedPipeline}
          />
        </div>

        <div style={{ marginTop: 5, width: 600, marginBottom: 11 }}>
          <label style={{
            marginTop: 11,
            fontWeight: 600,
            marginBottom: 4,
            display: 'block',
          }}>
            Verantwortlicher
          </label>
          <Select
            options={users.map(user => ({
              value: user.id,
              label: `${user.firstName || ''} ${user.lastName || ''}`.trim() || `#${user.id}`,
            }))}
            value={selectedResponsibleUser}
            onChange={setSelectedResponsibleUser}
            placeholder="Verantwortlichen wählen"
            isClearable
          />
          <label style={{
            marginTop: 14,
            fontWeight: 600,
            marginBottom: 4,
            display: 'block',
          }}>
            Bezeichnung Pipeline-Instanz
          </label>
          <input
            type="text"
            value={pipelineInstanceName}
            onChange={event => setPipelineInstanceName(event.target.value)}
            placeholder="z. B. Angebot Juni-Aktion"
            style={{
              width: '100%',
              padding: 8,
              borderRadius: 5,
              border: '1px solid #bbb',
              fontSize: 15,
              marginBottom: 4,
            }}
          />
        </div>
      </div>

      <div style={{ marginBottom: 20 }}>
        <h3>Empfänger filtern</h3>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, minmax(180px, 1fr))',
          gap: 10,
        }}>
          <Select
            options={serverFilterOptions.accountTypes}
            placeholder="Typ"
            isClearable
            value={filters.accountType}
            onChange={value => updateFilter('accountType', value)}
          />
          <Select
            options={serverFilterOptions.customerNumbers.map(value => ({
              value,
              label: String(value),
            }))}
            placeholder="Kundennummer"
            isClearable
            isDisabled={filters.accountType?.value === 'prospect'}
            value={filters.customerNumber}
            onChange={value => updateFilter('customerNumber', value)}
          />
          <Select
            options={serverFilterOptions.companies.map(value => ({ value, label: value }))}
            placeholder="Firma"
            isClearable
            value={filters.company}
            onChange={value => updateFilter('company', value)}
          />
          <Select
            options={serverFilterOptions.cities.map(value => ({ value, label: value }))}
            placeholder="Ort"
            isClearable
            value={filters.city}
            onChange={value => updateFilter('city', value)}
          />
          <Select
            options={serverFilterOptions.origins.map(value => ({
              value: value.id,
              label: value.name,
            }))}
            placeholder="Herkunft"
            isClearable
            value={filters.customerOrigin}
            onChange={value => updateFilter('customerOrigin', value)}
          />
          <Select
            options={serverFilterOptions.categories.map(value => ({
              value: value.id,
              label: value.name,
            }))}
            placeholder="Kategorie"
            isClearable
            value={filters.customerCategory}
            onChange={value => updateFilter('customerCategory', value)}
          />
          <Select
            options={serverFilterOptions.groups.map(value => ({
              value: value.id,
              label: value.name,
            }))}
            placeholder="Gruppe"
            isClearable
            value={filters.customerGroup}
            onChange={value => updateFilter('customerGroup', value)}
          />
          <Select
            options={serverFilterOptions.statuses.filter(option => (
              !filters.accountType || option.accountType === filters.accountType.value
            ))}
            placeholder="CRM-Status"
            isClearable
            value={filters.status}
            onChange={value => updateFilter('status', value)}
          />
        </div>
      </div>

      <table>
        <thead>
          <tr>
            <th>
              <input
                type="checkbox"
                checked={currentPageSelected}
                onChange={toggleAll}
                title="Alle gefilterten Empfänger auswählen"
              />
            </th>
            <th>Typ</th>
            <th>Referenz</th>
            <th>Firma</th>
            <th>PLZ</th>
            <th>Ort</th>
            <th>Herkunft</th>
            <th>Kategorie</th>
            <th>Gruppe</th>
            <th>CRM-Status</th>
            <th>E-Mail</th>
            <th>Telefon</th>
          </tr>
        </thead>
        <tbody>
          {!loading && recipients.length === 0 && (
            <tr>
              <td colSpan={12}>Keine passenden Empfänger gefunden.</td>
            </tr>
          )}
          {recipients.map(recipient => (
            <tr key={recipient.key}>
              <td>
                <input
                  type="checkbox"
                  checked={selected.includes(recipient.key)}
                  onChange={() => toggleSelect(recipient.key)}
                />
              </td>
              <td>{recipient.accountType === 'prospect' ? 'Interessent' : 'Kunde'}</td>
              <td>{recipient.reference}</td>
              <td style={recipient.accountType === 'prospect' ? { fontStyle: 'italic' } : undefined}>
                {recipient.company}
              </td>
              <td>{recipient.postalCode}</td>
              <td>{recipient.city}</td>
              <td>{recipient.origin?.name || '-'}</td>
              <td>{recipient.category?.name || '-'}</td>
              <td>{recipient.group?.name || '-'}</td>
              <td>{recipient.crmStatus || '-'}</td>
              <td>{recipient.email}</td>
              <td>{recipient.phone}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div style={{ marginTop: '1rem' }}>
        <button
          onClick={() => setCurrentPage(page => Math.max(1, page - 1))}
          disabled={currentPage === 1 || loading}
        >
          Vorherige
        </button>
        <span style={{ margin: '0 1rem' }}>
          Seite {currentPage} von {totalPages}
        </span>
        <button
          onClick={() => setCurrentPage(page => page + 1)}
          disabled={currentPage >= totalPages || loading}
        >
          Nächste
        </button>
      </div>

      <div style={{ marginTop: 18 }}>
        <button
          disabled={selected.length === 0 || mailing}
          onClick={handleSend}
          style={{
            background: '#2eae4a',
            color: '#fff',
            padding: '10px 22px',
            border: 'none',
            borderRadius: 5,
            fontSize: 18,
          }}
        >
          {mailing ? 'Versand läuft...' : `Mail an ${selected.length} Empfänger senden`}
        </button>
        {msg && (
          <span style={{
            marginLeft: 24,
            color: msg.startsWith('Fehler') ? 'red' : 'green',
          }}>
            {msg}
          </span>
        )}
      </div>
    </div>
  );
}

export default BulkMailTableForm;
